/*
 * EventyrCykler's indicative 24-payment quote adapter.
 * Calculation mirrors the official Anyday price-widget.js, verified 2026-10-12:
 * https://my.anyday.io/price-widget/anyday-price-widget.js
 * SHA-256: 798cc3add8fc3b99051b26de3b18d2d75d7584b545e5008e623cefcda5248d0f
 * Rates, eligibility, cutoff and banking dates come from Anyday, never a fixed
 * multiplier. Their popup remains the source of full terms and final estimates.
 * Callers must retain generic Anyday text if this returns null or rejects.
 */
(function (global) {
  'use strict';

  const TOKEN = 'f4c7de7ad1f647dd85ba23a694b67c97';
  const ENDPOINT = 'https://my.anyday.io/capi/v1/internal/installment/price-widget-data?token=' + TOKEN;
  const CACHE_KEY = 'anyday-total-price-data';
  const CACHE_MS = 6 * 60 * 60 * 1000;
  let cached = null;
  let pending = null;

  // Official installment rounding: round each accumulated share up to øre,
  // leaving the final payment to absorb the rounding difference.
  function paidBy(total, split, count) {
    if (count >= split) return total;
    const raw = total / split * 100;
    const rounded = Math.round(raw);
    const payment = (Math.abs(raw - rounded) < 1e-9 ? rounded : Math.ceil(raw)) / 100;
    return count * payment;
  }

  // Brent solver, with the same tolerance and termination as Anyday's widget.
  function root(fn, lower, upper, tolerance = 1e-8, maxIterations = 100) {
    let lowValue = fn(lower), highValue = fn(upper), value = highValue;
    let step = 0, previousStep = 0, current = upper, previousHalf = NaN;
    if (Math.sign(lowValue) === Math.sign(highValue)) {
      if (Math.abs(lowValue) < tolerance) return lower;
      if (Math.abs(highValue) < tolerance) return upper;
      throw new Error('Anyday fee root is not bracketed');
    }
    for (let iteration = 0; iteration <= maxIterations; iteration++) {
      if (Math.sign(value) === Math.sign(highValue)) {
        upper = lower; highValue = lowValue; previousStep = step = current - lower;
      }
      if (Math.abs(highValue) < Math.abs(value)) {
        lower = current; current = upper; upper = lower;
        lowValue = value; value = highValue; highValue = lowValue;
      }
      const epsilon = 2 * Math.pow(2, -53) * Math.abs(current) + 0.5 * tolerance;
      const lastHalf = previousHalf;
      previousHalf = (upper - current) / 2;
      if (Math.abs(previousHalf) <= epsilon || Math.abs(value) < tolerance) return current;
      if (previousHalf === lastHalf) throw new Error('Anyday fee solver stalled');
      if (Math.abs(previousStep) >= epsilon && Math.abs(lowValue) > Math.abs(value)) {
        const ratio = value / lowValue;
        let numerator, denominator;
        if (lower === upper) {
          numerator = 2 * previousHalf * ratio; denominator = 1 - ratio;
        } else {
          denominator = lowValue / highValue;
          const otherRatio = value / highValue;
          numerator = ratio * (2 * previousHalf * denominator * (denominator - otherRatio) - (current - lower) * (otherRatio - 1));
          denominator = (denominator - 1) * (otherRatio - 1) * (ratio - 1);
        }
        if (numerator > 0) denominator = -denominator;
        numerator = Math.abs(numerator);
        if (2 * numerator < Math.min(3 * previousHalf * denominator - Math.abs(epsilon * denominator), Math.abs(previousStep * denominator))) {
          previousStep = step; step = numerator / denominator;
        } else { step = previousHalf; previousStep = step; }
      } else { step = previousHalf; previousStep = step; }
      lower = current; lowValue = value;
      current += Math.abs(step) > epsilon ? step : (previousHalf >= 0 ? Math.abs(epsilon) : -Math.abs(epsilon));
      value = fn(current);
    }
    throw new Error('Anyday fee solver exceeded its iteration limit');
  }

  function presentValue(rate, cashFlow) {
    const sorted = [...cashFlow].sort((a, b) => a.date.getTime() - b.date.getTime());
    const start = sorted[0].date;
    return sorted.reduce((sum, entry) => sum + entry.payment / Math.pow(1 + rate, (entry.date.getTime() - start.getTime()) / 864e5 / 365), 0);
  }

  function feeFor(total, split, rate, dates) {
    if (rate === 0) return 0;
    const cashFlow = [{payment: total, date: dates[0]}];
    for (let count = 1; count <= split; count++) {
      cashFlow.push({payment: -(paidBy(total, split, count) - paidBy(total, split, count - 1)), date: dates[count - 1]});
    }
    const repaymentCount = cashFlow.filter(entry => entry.payment < 0).length;
    const incoming = cashFlow.filter(entry => entry.payment > 0).reduce((sum, entry) => sum + entry.payment, 0);
    const withFee = fee => cashFlow.map(entry => entry.payment < 0 ? {payment: entry.payment - fee, date: entry.date} : entry);
    const fee = root(amount => presentValue(rate, withFee(amount)), 0, incoming);
    return Math.floor(100 * fee) / 100 * repaymentCount;
  }

  function validateData(data) {
    if (!data || !Array.isArray(data.installmentTemplates) || !Array.isArray(data.lastBankDates) || !data.lastBankDates.length) throw new Error('Anyday data unavailable');
    if (data.cutoffDate != null && !Number.isFinite(Date.parse(data.cutoffDate))) throw new Error('Invalid Anyday cutoff date');
    let previous = -Infinity;
    for (const value of data.lastBankDates) {
      const timestamp = Date.parse(value);
      if (!Number.isFinite(timestamp) || timestamp <= previous) throw new Error('Invalid Anyday banking dates');
      previous = timestamp;
    }
    for (const template of data.installmentTemplates) {
      const settings = template && template.settings;
      if (!settings || !Number.isInteger(settings.split) || settings.split < 1 || !Number.isFinite(settings.annualPercentageRate) || settings.annualPercentageRate < 0 || !Number.isFinite(settings.minOrderAmount) || settings.minOrderAmount < 0) throw new Error('Invalid Anyday payment settings');
    }
    return data;
  }

  function calculate(data, priceInOre, now = new Date()) {
    validateData(data);
    const time = new Date(now);
    if (!Number.isFinite(time.getTime()) || !Number.isSafeInteger(priceInOre)) throw new Error('Invalid Anyday quote input');
    const cashPrice = priceInOre / 100;
    // The official widget does not render for a missing price or above DKK150,000.
    if (cashPrice <= 0 || cashPrice > 150000) return null;
    const template = data.installmentTemplates.find(item => item.settings.split === 24);
    if (!template || cashPrice < template.settings.minOrderAmount) return null;
    const settings = template.settings;
    const cutoffDay = data.cutoffDate ? new Date(data.cutoffDate).getDate() : null;
    const cutoff = cutoffDay == null ? null : new Date(time.getFullYear(), time.getMonth(), cutoffDay, time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
    const bankingDates = cutoff && cutoff.getTime() > time.getTime()
      ? data.lastBankDates.slice(0, settings.split - 1)
      : data.lastBankDates.slice(1, settings.split);
    if (bankingDates.length !== settings.split - 1 || bankingDates.some(date => Date.parse(date) <= time.getTime())) throw new Error('Anyday payment calendar is stale or incomplete');
    const dates = [time, ...bankingDates.map(date => new Date(date))];
    const totalFee = feeFor(cashPrice, settings.split, settings.annualPercentageRate, dates);
    const monthly = paidBy(cashPrice + totalFee, settings.split, 1);
    if (!Number.isFinite(monthly) || monthly <= 0 || !Number.isFinite(totalFee) || totalFee < 0) throw new Error('Invalid Anyday quote result');
    return {
      monthly, split: settings.split, cashPrice, totalFee,
      totalRepayment: cashPrice + totalFee,
      apr: settings.annualPercentageRate,
      aprPercent: settings.annualPercentageRate * 100,
      termMonths: settings.split - 1
    };
  }

  async function getData() {
    if (cached && cached.expires > Date.now()) return cached.data;
    // Honor the official widget's existing six-hour cache so cards and its
    // popup use the same merchant terms. Do not create any new stored data.
    try {
      const saved = JSON.parse(global.localStorage.getItem(CACHE_KEY));
      if (saved && saved.expires > Date.now() && saved.expires <= Date.now() + CACHE_MS) {
        validateData(saved.data); cached = saved; return saved.data;
      }
    } catch (_) { /* Storage is optional; fetch current official data instead. */ }
    if (pending) return pending;
    pending = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await global.fetch(ENDPOINT, {credentials: 'omit', signal: controller.signal});
        if (!response.ok) throw new Error('Anyday payment data could not be loaded');
        const data = validateData(await response.json());
        cached = {data, expires: Date.now() + CACHE_MS};
        return data;
      } finally { clearTimeout(timeout); }
    })();
    try { return await pending; } finally { pending = null; }
  }

  function withSignal(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
    return new Promise((resolve, reject) => {
      const abort = () => reject(new DOMException('Aborted', 'AbortError'));
      signal.addEventListener('abort', abort, {once: true});
      promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
    });
  }

  global.ECAnydayQuote = Object.freeze({
    calculate,
    async getQuote(priceInOre, options = {}) {
      if (options.signal && options.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const data = await withSignal(getData(), options.signal);
      return calculate(data, priceInOre, options.now == null ? new Date() : options.now);
    }
  });
})(window);
